import React from 'react';
import styled from 'styled-components';
import FinalSaleMessage from '../../../../../../common/FinalSaleMessage';
import Icon from '../../../../../../common/Icon';
import ProductBadge from '../../../../../../common/ProductBadge';
import { BodyFour, BodyTwo } from '@/styles/theme/default/typography';
import { getName, getPrice } from '@/utils/helper';
import Heart from '../../../../common/greyHeart/Heart';
import { H1, Tooltip } from '../../../Styles';
import Affirm from './Affirm';
import StarsInfo from './StarsInfo';
import Share from "@/components/customers/product/productBody/share/Share";
import useSale from '@/hooks/useSale';

const { isSale, code } = useSale();

const Wrapper = styled.div`
  padding: 30px 0 5px 0;
  margin: 0 20px;

  .saleBanner {
    font-family: Graphik;
    font-style: normal;
    font-weight: 600;
    font-size: 13px;
    line-height: 23px;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: #CE2C20;
  }

  .roll-txt {
    letter-spacing: 0;
    text-transform: lowercase;
    display: initial;
  }

  .title-heart {
    display: flex;
    align-items: center;
    justify-content: space-between;
    h1 {
      margin-bottom: 0;
      text-align: start;
    }
    .heart-container {
      margin-left: 10px;
    }
  }
  .price-saved-price {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin: 22px auto 7px auto;
  }

  .sale-copy {
    margin-bottom: 13px;
  }
  .details-shipping-container {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    margin-bottom: 0;

    .line {
      margin: 20px 10px 0 10px;
    }
  }

  .oversize-fragile-item-container {
    margin-bottom: 5px;
    display: flex;
    align-items: baseline;

    .oversize-fragile-price {
      margin-left: 6px;
    }

    .tooltip-container {
      position: relative;

      margin-left: 6px;

      .tooltip-customize {
        right: -78px;
        left: unset;
        width: 240px;
        top: 33px;

        .desc {
          text-align: start;
        }

        .tooltip-arrow {
          right: 75px;
          left: unset;
        }
      }

      &:hover {
        .tooltip-customize {
          transform: scale(1);
        }
      }
    }
  }

  .line-separator {
    width: 100%;
    border: 1px solid #d8d8d8;
    margin: 10px 0;
  }
`;

const Badge = styled.div`
  border: 1px solid #a83569;
  background: white;
  border-radius: 0 16px;
  color: #a83569;
  font-size: 13px;
  font-family: ${({ theme }) => theme.fonts.primary};
  font-style: normal;
  font-weight: 500;
  line-height: 16px;
  letter-spacing: 0.0007em;
  text-align: center;
  max-width: 290px;
  padding: 8px;
  margin-bottom: 10px;
`;

function ProductInfo({ name, price, fullPrice, productData, isMobile }) {
  price = parseFloat(price);
  fullPrice = parseFloat(fullPrice);
  const traditionalRetailPrice = parseInt(productData.traditional_retail_price);
  const isWallpaper = productData.frame?.type.id == 4;

  const isFabricByTheYard = [162].includes(parseInt(productData.frame.id));

  const priceText = isWallpaper ? (
    <BodyTwo className="roll-txt" bold={true}>
      {' '}
      / roll
    </BodyTwo>
  ) : isFabricByTheYard ? (
    <BodyTwo className="roll-txt" bold={true}>
      {' '}
      / yard
    </BodyTwo>
  ) : (
    ''
  );

  const saleBanner = (isSale && (productData.frame.type.name!=='Marketplace')) ? (
    <div className="saleBanner">
      {(getPrice(price) < 799) ? '20%' : '25%'} OFF WITH CODE {code}
    </div>
  ) : (
    ''
  );

  const calculateSavings = (price, fullPrice) => {
    return parseInt(Math.round(((fullPrice - price) / fullPrice) * 100));
  };

  const calculateDollarSavings = (price, traditionalRetailPrice) => {
    return traditionalRetailPrice - price;
  };

  return (
    <Wrapper>
      {productData.frame?.id == 78 && (
        <Badge>Replacement Slings Available for Purchase</Badge>
      )}

      {productData.badge_treatment && (
        <div className="mb-2">
          <ProductBadge badgeTreatment={productData.badge_treatment} />
        </div>
      )}

      <div className="title-heart">
        <H1>{getName(name)}</H1>
        {!isMobile && (
          <div className="heart-container hide-on-mobile">
            <Heart
              productName={productData.name}
              productSlug={productData.slug}
              productId={productData.id}
            />
          </div>
        )}
      </div>
      <StarsInfo productData={productData} />
      <div className="price-saved-price">
        {price < fullPrice ? (
          <>
            <FullPrice>${getPrice(fullPrice)}</FullPrice>
            <Price>
              ${getPrice(price)}
              {priceText}
            </Price>
            <Savings>(Save {calculateSavings(price, fullPrice)}%)</Savings>
          </>
        ) : traditionalRetailPrice && calculateDollarSavings(price, traditionalRetailPrice) > 0 ? (
          <>
            <FullPrice>${getPrice(traditionalRetailPrice)}</FullPrice>
            <Price>${getPrice(price)}</Price>
            <Savings>
              (You Save ${calculateDollarSavings(price, traditionalRetailPrice)}
              )
            </Savings>
          </>
        ) : (
          <SimplePrice>
            ${getPrice(price)}
            {priceText}
          </SimplePrice>
        )}

        <Share productData={productData} />
      </div>
      {/*{saleBanner}*/}
      {window.isSaleActive && productData?.frame?.is_final_sale == true && (
        <FinalSaleMessage color="#6C6C6C" />
      )}
      <Affirm price={price} />

      {productData?.frame?.oversize_fee > 0 && (
        <div className="oversize-fragile-item-container">
          <BodyFour>Oversize/Fragile Item</BodyFour>{' '}
          <div className="tooltip-container">
            <Icon icon="ti-tooltip-info" size={20} color="#2A5CAF" />
            <Tooltip className="tooltip-customize">
              <div className="tooltip-arrow" />
              <p className="desc">
                This item is oversized and requires extra care to be delivered
                right to your door.
              </p>
            </Tooltip>
          </div>
          <BodyFour className="oversize-fragile-price">
            +${getPrice(productData?.frame?.oversize_fee)}
          </BodyFour>
        </div>
      )}

      <div className="line-separator hide-on-mobile" />
    </Wrapper>
  );
}

export default ProductInfo;

const BasePrice = styled.div`
  font-family: ${({ theme }) => theme.fonts.primary};
  font-style: normal;
  font-size: 18px;
  line-height: 28px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
`

const FullPrice = styled(BasePrice)`
  line-height: 28px;
  margin-right: 12px;
  text-decoration-line: line-through;
  color: ${({ theme }) => theme.colors.charcoal};
`;

const SimplePrice = styled(BasePrice)`
  font-weight: 600;
  color: ${({ theme }) => theme.colors.charcoal};
`;

const Price = styled(BasePrice)`
  font-weight: 600;
  color: ${({ theme }) => theme.colors.french};
`;

const Savings = styled.div`
  margin-top: 22px;
  font-family: ${({ theme }) => theme.fonts.primary};
  font-style: normal;
  font-size: 16px;
  line-height: 23px;
  margin-left: 8px;
  color: ${({ theme }) => theme.colors.french};
  opacity: 0.9;
`;
